import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { Plus, Trash2, CalendarClock, AlertTriangle, PlayCircle, Hourglass, CheckCircle2, ShieldCheck, Timer, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { RichTextEditor } from '../components/RichTextEditor';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { fmtDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import TaskDetailSheet, { statusOf } from '../components/tasks/TaskDetailSheet';
import { UserMultiSelect } from '../components/UserMultiSelect';

const COLUMNS = [
  { key: 'not_started', label: 'Not Started', statuses: ['not_started'] },
  { key: 'in_progress', label: 'In Progress', statuses: ['in_progress', 'reopened'] },
  { key: 'waiting', label: 'Waiting', statuses: ['waiting_info', 'waiting_approval', 'on_hold', 'waiting'] },
  { key: 'completed', label: 'Completed', statuses: ['completed', 'approved'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['cancelled'] },
];

const PRIORITY_DOT = { high: 'bg-magenta', medium: 'bg-amber-400', low: 'bg-slate-300' };

function MetricChip({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 min-w-[140px]" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}><Icon className="w-4 h-4" /></div>
      <div>
        <div className="font-heading text-xl font-bold text-navy leading-none">{value ?? '—'}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [open, setOpen] = useState(false);
  const [task, setTask] = useState({ title: '', assigned_to: '', due_date: '', priority: 'medium', status: 'not_started', category: '', notes: '' });
  const [detailId, setDetailId] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = ['leadership', 'director'].includes(user?.role);

  const load = () => {
    api.get('/tasks').then((r) => setTasks(r.data));
    api.get('/tasks/metrics/summary').then((r) => setMetrics(r.data));
  };
  useEffect(() => {
    load();
    api.get('/users').then((r) => setUsers(r.data));
  }, []);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setDetailId(id);
      setDetailOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const save = async () => {
    if (!task.title || task.assignees.length === 0) return toast.error('Title and at least one assignee required');
    await api.post('/tasks', { ...task, assigned_to: task.assignees[0] });
    setOpen(false);
    setTask({ title: '', assigned_to: '', assignees: [], due_date: '', priority: 'medium', status: 'not_started', category: '', notes: '' });
    toast.success('Task created');
    load();
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/tasks/${id}`);
    toast.success('Deleted');
    load();
  };

  const openDetail = (id) => { setDetailId(id); setDetailOpen(true); };

  const grouped = COLUMNS.map((c) => ({ ...c, items: tasks.filter((t) => c.statuses.includes(t.status)) }));
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* My Tasks metrics */}
      {metrics && (
        <div className="space-y-3">
          <div className="flex gap-3 flex-wrap">
            <MetricChip icon={CalendarClock} label="Due Today" value={metrics.my.due_today} accent="bg-blue-100 text-blue-600" />
            <MetricChip icon={AlertTriangle} label="Overdue" value={metrics.my.overdue} accent="bg-rose-100 text-rose-600" />
            <MetricChip icon={PlayCircle} label="In Progress" value={metrics.my.in_progress} accent="bg-cyan-100 text-cyan-600" />
            <MetricChip icon={Hourglass} label="Waiting" value={metrics.my.waiting} accent="bg-amber-100 text-amber-600" />
            <MetricChip icon={CheckCircle2} label="Completed This Week" value={metrics.my.completed_this_week} accent="bg-emerald-100 text-emerald-600" />
          </div>
          {metrics.team && (
            <div className="flex gap-3 flex-wrap items-stretch" data-testid="team-metrics">
              <MetricChip icon={ShieldCheck} label="Waiting Approval" value={metrics.team.waiting_approval} accent="bg-violet-100 text-violet-600" />
              <MetricChip icon={AlertTriangle} label="Team Overdue" value={metrics.team.overdue} accent="bg-rose-100 text-rose-600" />
              <MetricChip icon={CheckCircle2} label="Team Completed (wk)" value={metrics.team.recently_completed} accent="bg-emerald-100 text-emerald-600" />
              <MetricChip icon={Timer} label="Avg Completion (hrs)" value={metrics.team.avg_completion_hours} accent="bg-slate-100 text-slate-600" />
              {metrics.team.workload?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-2.5 flex-1 min-w-[220px]" data-testid="workload-card">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mb-1"><Users className="w-3.5 h-3.5" /> Workload by Team Member</div>
                  <div className="flex gap-3 flex-wrap">
                    {metrics.team.workload.slice(0, 6).map((w) => (
                      <span key={w.user_id} className="text-xs text-navy"><strong>{w.name?.split(' ')[0]}</strong> · {w.open_tasks}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-magenta hover:bg-magenta/90" data-testid="new-task-button"><Plus className="w-4 h-4 mr-2" />New Task</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Task</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Title *</Label><Input value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} data-testid="task-title" /></div>
              <div>
                <Label className="text-xs">Assigned To * (one or more)</Label>
                <UserMultiSelect users={users} value={task.assignees} onChange={(v) => setTask({ ...task, assignees: v })} testId="task-assignees" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={task.priority} onValueChange={(v) => setTask({ ...task, priority: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Due Date</Label><Input type="datetime-local" onChange={(e) => setTask({ ...task, due_date: new Date(e.target.value).toISOString() })} /></div>
              </div>
              <div><Label className="text-xs">Category</Label><Input value={task.category} onChange={(e) => setTask({ ...task, category: e.target.value })} /></div>
              <div><Label className="text-xs">Description</Label><div data-testid="task-notes"><RichTextEditor value={task.notes} onChange={(v) => setTask({ ...task, notes: v })} minHeight={110} label="Task Description" testId="task-notes-editor" /></div></div>
            </div>
            <DialogFooter><Button onClick={save} className="bg-magenta hover:bg-magenta/90" data-testid="save-task">Create Task</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {grouped.map((g) => (
          <div key={g.key} className="bg-slate-50 rounded-xl p-3 min-h-[400px]" data-testid={`task-col-${g.key}`}>
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="font-heading font-bold text-sm text-navy">{g.label}</span>
              <span className="text-xs text-slate-500">{g.items.length}</span>
            </div>
            <div className="space-y-2">
              {g.items.map((t) => {
                const ids = t.assignees?.length ? t.assignees : [t.assigned_to];
                const names = ids.map((id) => users.find((u) => u.id === id)?.full_name?.split(' ')[0]).filter(Boolean);
                const st = statusOf(t.status);
                const overdue = t.due_date && t.due_date.slice(0, 10) < today && !['completed', 'approved', 'cancelled'].includes(t.status);
                return (
                  <div key={t.id} onClick={() => openDetail(t.id)}
                    className="bg-white rounded-lg p-3 border border-slate-200 shadow-sm cursor-pointer hover:border-magenta/40 hover:shadow transition"
                    data-testid={`task-${t.id}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`w-2 h-2 rounded-full ${PRIORITY_DOT[t.priority]}`} />
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t.category || 'Task'}</span>
                      {isAdmin && (
                        <button onClick={(e) => remove(e, t.id)} className="ml-auto text-slate-300 hover:text-rose-500" data-testid={`task-delete-${t.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    <div className="font-semibold text-sm text-navy mb-2 leading-snug">{t.title}</div>
                    <div className="flex items-center justify-between text-[11px] text-slate-500 mb-2">
                      <span className="truncate mr-1">{names.length > 2 ? `${names.slice(0, 2).join(', ')} +${names.length - 2}` : names.join(', ') || '—'}{names.length > 1 && <span className="ml-1 text-magenta font-bold">· Shared</span>}</span>
                      <span className={overdue ? 'text-rose-500 font-semibold' : ''}>{fmtDate(t.due_date)}</span>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${st.cls}`}>{st.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <TaskDetailSheet
        taskId={detailId}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        users={users}
        currentUser={user}
        onChanged={load}
      />
    </div>
  );
}
